<template>
  <div class="certificates-container">
    <div class="section-inner">
      <!-- Tab Header -->
      <div class="tab-bar">
        <div class="tab-item active">Certificates</div>
      </div>

      <div v-loading="loading" style="min-height: 300px;">
        <div v-if="certificates.length > 0" class="cards-split-view">
          <!-- Left Panel: Certificate List -->
          <div class="cards-list-panel">
            <div
              v-for="(cert, idx) in certificates"
              :key="cert.certificate_id"
              class="card-list-item"
              :class="{ active: selectedIndex === idx }"
              @click="selectedIndex = idx"
            >
              <div class="card-list-cover">
                <CachedImage
                  v-if="cert.cover_url"
                  :src="cert.cover_url"
                  :fallback-src="placeholderImage"
                  fit="cover"
                  width="100%"
                  height="100%"
                />
                <div v-else class="mini-cover-placeholder">
                  <i class="el-icon-trophy"></i>
                </div>
              </div>
              <div class="card-list-info">
                <span class="card-list-deck-name">{{ cert.collection_title }}</span>
                <span class="card-list-card-title">{{ formatDate(cert.issue_date) }}</span>
              </div>
              <div class="card-list-score">
                <span class="score-badge">{{ formatScore(cert) }}</span>
              </div>
            </div>
          </div>

          <!-- Right Panel: Certificate Preview -->
          <div class="card-content-panel">
            <template v-if="selectedCert">
              <div class="certificate-preview">
                <div class="certificate-frame" :style="{ backgroundImage: 'url(' + certBg + ')' }">
                  <div class="cert-content">
                    <!-- Recipient -->
                    <p class="cert-present">This is to certify that</p>
                    <h2 class="cert-name">{{ selectedCert.recipient_name }}</h2>

                    <!-- Course -->
                    <p class="cert-body">has successfully completed the course</p>
                    <h3 class="cert-course">"{{ selectedCert.collection_title }}"</h3>

                    <!-- Score -->
                    <p class="cert-score" v-if="selectedCert.show_score">
                      with a score of <strong>{{ formatScore(selectedCert) }}</strong>
                    </p>

                    <div style="margin-top: 80px;"></div>

                    <!-- Footer info -->
                    <div class="cert-footer">
                      <div class="cert-footer-item">
                        <span class="cert-footer-value">{{ formatDate(selectedCert.issue_date) }}</span>
                        <div class="cert-footer-line"></div>
                        <span class="cert-footer-label">DATE</span>
                      </div>
                      <div class="cert-footer-item">
                        <span class="cert-footer-value"><i>LearningFirst</i></span>
                        <div class="cert-footer-line"></div>
                        <span class="cert-footer-label">ISSUED BY</span>
                      </div>
                    </div>

                    <!-- Certificate Code -->
                    <p class="cert-code">{{ selectedCert.certificate_code }}</p>
                  </div>
                </div>
              </div>
              <div class="card-content-footer">
                <div></div>
                <el-button size="small" type="primary" @click="downloadCertificate">
                  Download
                </el-button>
              </div>
            </template>
            <div v-else class="empty-state" style="padding: 60px 20px;">
              <i class="el-icon-document"></i>
              <p>Select a certificate to preview</p>
            </div>
          </div>
        </div>
        <div v-if="!loading && certificates.length === 0" class="empty-state">
          <i class="el-icon-trophy"></i>
          <h3>No certificates yet</h3>
          <p>Pass an assessment exam to earn a certificate</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CachedImage from '@/components/CachedImage.vue'
import { postRequest } from '@/apis/front'
import { mapState } from 'vuex'
import branding from '@/config/branding'
import certBg from '@/assets/certifi.png'

export default {
  name: 'MyCertificates',
  components: { CachedImage },
  data() {
    return {
      loading: false,
      placeholderImage: require('@/assets/Card Collection Cover Placeholder.jpeg'),
      certificates: [],
      selectedIndex: 0,
      brandLogo: branding.logo,
      certBg
    }
  },
  computed: {
    ...mapState(['user_id']),
    selectedCert() {
      return this.certificates[this.selectedIndex] || null
    }
  },
  mounted() {
    this.fetchCertificates()
  },
  methods: {
    async fetchCertificates() {
      this.loading = true
      try {
        const response = await postRequest('getMyCertificates', {
          user_id: this.user_id
        })
        if (response.data.code === 0) {
          this.certificates = response.data.data?.certificates || []
          this.selectedIndex = 0
        }
      } catch (error) {
        console.error('Error fetching certificates:', error)
        this.$message.error('Failed to load certificates')
      } finally {
        this.loading = false
      }
    },
    formatScore(cert) {
      if (cert.correct_count != null && cert.total_questions != null) {
        return cert.correct_count + '/' + cert.total_questions
      }
      // Fallback for old certificates: derive correct_count from score_percentage
      if (cert.score_percentage != null && cert.total_questions != null) {
        const correct = Math.round(cert.score_percentage * cert.total_questions / 100)
        return correct + '/' + cert.total_questions
      }
      return cert.score_percentage != null ? cert.score_percentage + '%' : '--'
    },
    formatDate(timestamp) {
      if (!timestamp) return ''
      return new Date(timestamp * 1000).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      })
    },
    downloadCertificate() {
      if (!this.selectedCert) return
      const el = this.$el.querySelector('.certificate-frame')
      if (!el) return

      import('html2canvas').then(({ default: html2canvas }) => {
        html2canvas(el, { scale: 2, backgroundColor: null, useCORS: true }).then(canvas => {
          const link = document.createElement('a')
          link.download = `Certificate-${this.selectedCert.collection_title}.png`
          link.href = canvas.toDataURL('image/png')
          link.click()
        })
      }).catch(() => {
        this.$message.info('Download feature requires html2canvas. Please install it: npm install html2canvas')
      })
    }
  }
}
</script>

<style scoped>
.certificates-container {
  width: 100%;
  min-height: 100vh;
  background: #f7f8fc;
}

.section-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Tab Bar */
.tab-bar {
  display: flex;
  gap: 0;
  padding: 24px 0 20px;
}

.tab-item {
  padding: 8px 28px;
  font-size: 15px;
  font-weight: 600;
  color: #909399;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}

.tab-item.active {
  color: #409eff;
  border-bottom-color: #409eff;
}

/* Split View */
.cards-split-view {
  display: flex;
  gap: 0;
  height: calc(100vh - 140px);
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.cards-list-panel {
  width: 420px;
  min-width: 420px;
  border-right: 1px solid #ebeef5;
  overflow-y: auto;
}

.card-list-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
  cursor: pointer;
  border-bottom: 1px solid #f2f3f5;
  transition: background 0.15s;
}

.card-list-item:hover {
  background: #f5f7fa;
}

.card-list-item.active {
  background: #ecf5ff;
  border-left: 3px solid #409eff;
  padding-left: 13px;
}

.card-list-cover {
  width: 56px;
  height: 56px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  background: #eef1f6;
}

.mini-cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8ecf1 0%, #d5dce6 100%);
}

.mini-cover-placeholder i {
  font-size: 18px;
  color: #b0bac5;
}

.card-list-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-list-deck-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-list-card-title {
  font-size: 12px;
  color: #909399;
}

.card-list-score {
  flex-shrink: 0;
}

.score-badge {
  font-size: 18px;
  font-weight: 700;
  color: #E6A23C;
}

/* Certificate Preview */
.card-content-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-width: 0;
}

.certificate-preview {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: #f0f2f5;
}

/* Portrait certificate frame */
.certificate-frame {
  width: 420px;
  aspect-ratio: 1786 / 2526;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cert-content {
  text-align: center;
  padding: 250px 44px 44px;
  width: 100%;
}

.cert-logo-area {
  margin-bottom: 20px;
}

.cert-logo {
  height: 48px;
  object-fit: contain;
}

.cert-label {
  font-size: 22px;
  font-weight: 800;
  color: #2563EB;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin: 0;
  font-family: 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
  font-style: italic;
  line-height: 1.3;
}

.cert-divider {
  width: 48px;
  height: 3px;
  background: #2563EB;
  margin: 12px auto 0;
}

.cert-present {
  font-size: 13px;
  color: #94A3B8;
  margin: 24px 0 8px;
  font-family: 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
  font-style: italic;
}

.cert-name {
  font-size: 30px;
  font-weight: 1000;
  color: #1A202C;
  margin: 0 0 18px;
  font-family: 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
  line-height: 1.3;
  margin-bottom: 10px;
}

.cert-body {
  font-size: 14px;
  color: #A0AEC0;
  margin: 0 0 8px;
  font-family: 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
}

.cert-course {
  font-size: 18px;
  font-weight: 800;
  color: #2D3748;
  margin: 0 0 8px;
  font-family: 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
  line-height: 1.4;
  margin-top: 10px;
}

.cert-score {
  font-size: 14px;
  color: #718096;
  margin: 0;
  font-family: 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
}

.cert-score strong {
  color: #2563EB;
  font-size: 16px;
}

.cert-footer {
  display: flex;
  justify-content: center;
  gap: 48px;
}

.cert-footer-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100px;
}

.cert-footer-value {
  font-size: 13px;
  color: #1E293B;
  font-weight: 700;
  font-family: 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
}

.cert-footer-line {
  width: 100%;
  height: 1px;
  background: #1E293B;
  margin: 6px 0 4px;
}

.cert-footer-label {
  font-size: 10px;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-family: 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
}

.cert-code {
  font-size: 9px;
  color: #CBD5E0;
  margin: 36px 0 0;
  letter-spacing: 0.15em;
  font-family: 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
}

/* Footer */
.card-content-footer {
  padding: 12px 32px;
  border-top: 1px solid #f2f3f5;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 56px;
  color: #dcdfe6;
}

.empty-state h3 {
  margin: 16px 0 8px;
  color: #606266;
  font-weight: 600;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 768px) {
  .cards-split-view {
    flex-direction: column;
    height: auto;
  }
  .cards-list-panel {
    width: 100%;
    min-width: 0;
    max-height: 300px;
    border-right: none;
    border-bottom: 1px solid #ebeef5;
  }
  .certificate-preview {
    padding: 16px;
  }
  .certificate-frame {
    width: 100%;
    max-width: 420px;
  }
  .cert-content {
    padding: 28px 24px 28px;
  }
  .cert-name {
    font-size: 20px;
  }
  .cert-label {
    font-size: 12px;
    letter-spacing: 0.15em;
  }
  .cert-course {
    font-size: 14px;
  }
  .cert-footer {
    gap: 32px;
  }
  .section-inner {
    padding: 0 16px;
  }
}
</style>
